// Клиент API поверх сгенерированной схемы (openapi-fetch). Ошибки — единая форма сервера
// {"error": {code, message, details, request_id}} → ApiError; 401 → выход из сессии.
import createClient, { type Middleware } from "openapi-fetch";

import { getToken } from "@/auth/token";

import type { paths } from "./schema";

export interface ApiErrorBody {
  error: { code: string; message: string; details: Record<string, unknown>; request_id: string | null };
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details: Record<string, unknown> = {},
    public readonly requestId: string | null = null,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/** Куда сообщить о 401: приложение вешает сюда выход из сессии. */
export const unauthorized: { handler: (() => void) | null } = { handler: null };

const auth: Middleware = {
  async onRequest({ request }) {
    const token = getToken();
    if (token) request.headers.set("Authorization", `Bearer ${token}`);
    return request;
  },
};

// Тот же origin, что у страницы (в проде админку отдаёт сам API под /ui; в dev — прокси Vite).
// Абсолютный baseUrl нужен Request в Node (тесты); fetch берётся при вызове — тесты подменяют его.
const baseUrl = typeof location !== "undefined" ? location.origin : "http://localhost";
export const client = createClient<paths>({ baseUrl, fetch: (request) => globalThis.fetch(request) });
client.use(auth);

interface FetchResult<T> {
  data?: T;
  error?: unknown;
  response: Response;
}

/** Распаковать ответ openapi-fetch: данные либо ApiError. */
export async function unwrap<T>(call: Promise<FetchResult<T>>): Promise<T> {
  const { data, error, response } = await call;
  if (response.ok) return data as T;
  throw toApiError(response, error);
}

export function toApiError(response: Response, body: unknown): ApiError {
  const err = (body as Partial<ApiErrorBody> | undefined)?.error;
  const apiError = new ApiError(
    response.status,
    err?.code ?? `http_${response.status}`,
    err?.message ?? response.statusText ?? "ошибка запроса",
    err?.details ?? {},
    err?.request_id ?? response.headers.get("x-request-id"),
  );
  if (response.status === 401) unauthorized.handler?.();
  return apiError;
}

export function isApiError(value: unknown): value is ApiError {
  return value instanceof ApiError;
}

export function errorText(value: unknown): string {
  if (isApiError(value))
    return value.code === value.message ? value.code : `${value.message} (${value.code})`;
  if (value instanceof Error) return value.message;
  return String(value);
}
